import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import { Pill, Building2, FileText, Download, Globe, Phone, MapPin, BadgeCheck } from "lucide-react";

/**
 * Public "Our Pharmacy Partners" page — lists active pharmacies from the
 * `pharmacies` collection (admin-managed). Seeded with Polomar as the first
 * record so the page is never blank.
 */
export default function PharmacyInfoPage() {
  const [rows, setRows] = useState([]);
  const [docsByPharm, setDocsByPharm] = useState({});
  const [legacyDocs, setLegacyDocs] = useState([]);

  useEffect(() => {
    API.get("/pharmacies").then(async ({ data }) => {
      setRows(data);
      // Fetch per-pharmacy documents in parallel
      const entries = await Promise.all(
        data.map(async (p) => {
          try {
            const { data: detail } = await API.get(`/pharmacies/${p.id}`);
            return [p.id, detail.documents || []];
          } catch {
            return [p.id, []];
          }
        }),
      );
      const map = Object.fromEntries(entries);
      setDocsByPharm(map);
    }).catch(() => {});
    // Backward-compat: still surface any legacy pharmacy_info_docs while
    // migrations complete.
    API.get("/pharmacy-info/documents").then(({ data }) => setLegacyDocs(data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pharmacy-info-page">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>GLP<span className="text-purple-600">Flo</span></span>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center"><Building2 className="w-7 h-7 text-purple-600" /></div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Our Pharmacy Partners</h1>
            <p className="text-slate-500">State-licensed 503A compounding pharmacies we work with.</p>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="bg-white rounded-2xl border border-purple-100 p-8 text-center text-slate-500">
            Pharmacy partner information will be posted here soon. Please check back.
          </div>
        ) : (
          <div className="space-y-8">
            {rows.map((p) => {
              const docs = docsByPharm[p.id] || [];
              return (
                <section
                  key={p.id}
                  id={p.slug}
                  className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 lg:p-10"
                  data-testid={`pharmacy-card-${p.slug}`}
                >
                  <div className="flex items-start gap-6 flex-wrap">
                    <div className="w-20 h-20 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {p.logo_url ? (
                        <img src={p.logo_url} alt={p.name} className="w-full h-full object-cover" />
                      ) : (
                        <Building2 className="w-8 h-8 text-purple-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.name}</h2>
                      {p.tagline && <p className="text-slate-500 text-sm mt-0.5">{p.tagline}</p>}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-3">
                        {(p.address_city || p.address_state) && (
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />
                            {[p.address_city, p.address_state].filter(Boolean).join(", ")}
                          </span>
                        )}
                        {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{p.phone}</span>}
                        {p.website && (
                          <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-purple-600 hover:underline">
                            <Globe className="w-3.5 h-3.5" />{p.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                        {p.license_number && (
                          <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-emerald-500" />
                            License {p.license_number}{p.license_state ? ` (${p.license_state})` : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {p.bio && (
                    <div className="mt-6 text-slate-600 leading-relaxed whitespace-pre-line">
                      {p.bio}
                    </div>
                  )}

                  {p.capabilities && p.capabilities.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-slate-900 mb-2">Capabilities</h3>
                      <div className="flex flex-wrap gap-2">
                        {p.capabilities.map((c, i) => (
                          <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {docs.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Documents & Resources</h3>
                      <div className="space-y-2">
                        {docs.map((d) => (
                          <div key={d.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-purple-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-slate-900 truncate">{d.filename}</p>
                                <p className="text-xs text-slate-400">{(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <a href={`/api/pharmacies/${p.id}/documents/${d.id}/download`} className="text-purple-600 hover:text-purple-700">
                              <Download className="w-5 h-5" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {/* Legacy combined-docs surface (hidden unless still present) */}
        {legacyDocs.length > 0 && (
          <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Additional pharmacy resources</h3>
            <div className="space-y-2">
              {legacyDocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <p className="font-medium text-sm text-slate-900 truncate">{d.filename}</p>
                  </div>
                  <a href={`/api/pharmacy-info/documents/${d.id}/download`} className="text-purple-600 hover:text-purple-700">
                    <Download className="w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Patient pharmacy-choice disclaimer — present but understated */}
        <p
          className="mt-12 text-[11px] leading-relaxed text-slate-400 max-w-3xl"
          data-testid="pharmacy-choice-disclaimer"
        >
          You always have the right to choose your own pharmacy. GLPFlo currently routes prescriptions
          only to Polomar Specialty Pharmacy (our compounding pharmacy partner). If you prefer a different pharmacy,
          you may be referred to another clinic that can send your prescription there. See our{" "}
          <Link to="/terms" className="underline hover:text-slate-500">Terms of Service</Link> for details.
        </p>
      </div>
    </div>
  );
}
